import React, { useEffect, useState } from "react";
import {
  Flex,
  Box,
  Button,
  HStack,
  Text,
  Heading,
  IconButton,
  Tooltip,
  Spinner,
  Icon,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { updatePatientAction } from "../../../../actions/patientActions";
import {
  backgroundLight,
  textSecondary,
  white,
  primary,
  buttonRed,
  buttonRedText,
  primaryAlt,
} from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import { FaArrowLeftLong, FaCircleInfo, FaXmark } from "react-icons/fa6";
import dayjs from "dayjs";
import FormControlDefault from "../../../../components/input/FormControlDefault";
import FormControlDatePicker from "../../../../components/input/FormControlDatePicker";
import FormControlPhone from "../../../../components/input/FormControlPhone";
import FormControlCountry from "../../../../components/input/FormControlCountry";
import { UPDATE_PATIENT_RESET } from "../../../../constants/patientConstants";
import useThemeColors from "../../../../hooks/useThemeColors";
import { motion } from "framer-motion";
import StatusButtonNew from "../../../../components/buttons/StatusButtonNew";
import FormControlGender from "../../../../components/input/FormControlGender";
import FormControlRepresentativeType from "../../../../components/input/FormControlRepresentativeType";
import useIsMobile from "../../../../hooks/useIsMobile";
import FormControlAddressFull from "../../../../components/input/FormControlAddressFull";
import useDebounce from "../../../../hooks/useDebounce";
import { useParams } from "react-router-dom";

const EditPatientNew = ({ patient, close }) => {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  //COLORS
  const { whiteMode, newBorderMode, textPrimaryMode, backgroundLightMode } =
    useThemeColors();

  // MOBILE
  const isMobile = useIsMobile();

  // TENANT DEFAULTS
  const [tenantAdultAge, setTenantAdultAge] = useState(18);

  // CONSTANTS
  const [patientId, setPatientId] = useState(patient?._id || "");
  const [firstName, setFirstName] = useState(patient?.firstName || "");
  const [lastName, setLastName] = useState(patient?.lastName || "");
  const [email, setEmail] = useState(patient?.email || "");
  const [patientID, setPatientID] = useState(patient?.patientID || "");
  const [birthDate, setBirthDate] = useState(patient?.birthDate || "");
  const [phone, setPhone] = useState(patient?.phone || "");
  const [phoneAlt, setPhoneAlt] = useState(patient?.phoneAlt || "");
  const [gender, setGender] = useState(patient?.gender || "");
  const [personalCode, setPersonalCode] = useState(patient?.personalCode || "");
  const [status, setStatus] = useState(patient?.status || "");

  const [address, setAddress] = useState(() => ({
    street: patient?.address?.[0]?.street || "",
    city: patient?.address?.[0]?.city || "",
    country: patient?.address?.[0]?.country || "",
    zip: patient?.address?.[0]?.zip || "",
    state: patient?.address?.[0]?.state || "",
    display: patient?.address?.[0]
      ? `${patient.address[0].street}, ${patient.address[0].city}, ${patient.address[0].zip}`
      : "",
  }));

  const [child, setChild] = useState(patient?.child || false);

  // REPRESENTATIVE
  const [representativeFirstName, setRepresentativeFirstName] = useState(
    patient.representative ? patient.representative.firstName : ""
  );
  const [representativeLastName, setRepresentativeLastName] = useState(
    patient.representative ? patient.representative.lastName : ""
  );
  const [representativePhone, setRepresentativePhone] = useState(
    patient.representative ? patient.representative.phone : ""
  );
  const [representativeEmail, setRepresentativeEmail] = useState(
    patient.representative ? patient.representative.email : ""
  );
  const [representativeType, setRepresentativeType] = useState(
    patient.representative ? patient.representative.type : ""
  );

  const [manualAddressVisible, setManualAddressVisible] = useState(false);
  const [showRepresentative, setShowRepresentative] = useState(
    patient.child || false
  );

  // DEBOUNCED BIRTHDATE
  const debouncedBirthDate = useDebounce(birthDate, 1000); // Add debounce with 500ms delay

  // LOADING
  const [loading, setLoading] = useState(false);
  const [loadingDisplay, setLoadingDisplay] = useState(true);

  // REDUX
  const updatePatient = useSelector((state) => state.updatePatient);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = updatePatient;

  // ERRORS
  const isFirstNameError = firstName === "";
  const isLastNameError = lastName === "";
  const isBirthDateError = birthDate === "";
  const isPhoneError = phone.length < 4;
  const isPhoneShortError = phone.length < 6;
  const isGenderError = gender === "";

  const isEmailValidated = !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(
    email
  );

  const isRepresentativeInfoError =
    representativeFirstName === "" ||
    representativeLastName === "" ||
    representativePhone === "" ||
    representativeEmail === "";

  const isAddressError =
    address.street === "" ||
    address.zip === "" ||
    address.city === "" ||
    address.country === "" ||
    address.state === "";

  const isFullDateEntered = (dateString) => {
    const date = new Date(dateString);
    return (
      date instanceof Date &&
      !isNaN(date) &&
      date.getFullYear().toString().length === 4 // Ensure full year is entered
    );
  };

  // BIRTHDATE
  const minBirthDate = new Date(1900, 0, 1); // Set minimum date to Jan 1, 1900
  const maxBirthDate = new Date(); // Set maximum date to today

  // FUNCTIONS

  const calculateAge = (birthDate) => {
    const birthday = dayjs(birthDate);
    const age = dayjs().diff(birthday, "year");
    return age;
  };

  // HANDLERS
  const toggleManualAddressVisible = () => {
    setManualAddressVisible(!manualAddressVisible);
  };

  const handleGenderChange = (selectedOption) => {
    setGender(selectedOption.value);
  };

  const handleRepresentativeTypeChange = (selectedOption) => {
    setRepresentativeType(selectedOption.value);
  };

  // Handler for when an address is selected
  const handleAddressSelect = (selectedAddress) => {
    setAddress({
      street: selectedAddress.street,
      city: selectedAddress.city,
      country: selectedAddress.country,
      zip: selectedAddress.zip,
      state: selectedAddress.state,
      display: `${selectedAddress.street}, ${selectedAddress.city}, ${selectedAddress.zip}`,
    });
  };

  const submitHandler = (e) => {
    e.preventDefault();

    const patient = {
      _id: patientId,
      firstName,
      lastName,
      birthDate,
      personalCode,

      gender,
      representative: {
        firstName: representativeFirstName,
        lastName: representativeLastName,
        phone: representativePhone,
        email: representativeEmail,
        type: representativeType,
      },
      phone,
      phoneAlt,
      email,
      address: [
        {
          street: address.street || "",
          city: address.city || "",
          country: address.country || "",
          zip: address.zip || "",
          state: address.state || "",
        },
      ],
      child,
      status,
    };

    dispatch(updatePatientAction(patient, slug));
  };

  const closeHandler = () => {
    close();
    dispatch({ type: UPDATE_PATIENT_RESET });
  };

  // USEEFFECT

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoadingDisplay(false);
    }, 300); // CHANGE THIS TO HAVE A LONGER LOADING TIME

    // Cleanup function to clear the timeout if the component unmounts
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (patient) {
      setPatientId(patient._id);
      setFirstName(patient.firstName);
      setLastName(patient.lastName);
      setEmail(patient.email);
      setPatientID(patient.patientID);
      setBirthDate(patient.birthDate);
      setPhone(patient.phone);
      setPhoneAlt(patient.phoneAlt);
      setGender(patient.gender);
      setPersonalCode(patient.personalCode);
      setStatus(patient.status);

      if (patient.child) {
        setRepresentativeFirstName(
          patient && patient.representative && patient.representative.firstName
        );
        setRepresentativeLastName(
          patient && patient.representative && patient.representative.lastName
        );
        setRepresentativePhone(
          patient && patient.representative && patient.representative.phone
        );
        setRepresentativeEmail(
          patient && patient.representative && patient.representative.email
        );
        setRepresentativeType(
          patient && patient.representative && patient.representative.type
        );
      }

      const addr = patient.address?.[0] || {};
      setAddress({
        street: addr.street || "",
        city: addr.city || "",
        country: addr.country || "",
        zip: addr.zip || "",
        state: addr.state || "",
        display:
          addr.street || addr.city || addr.zip
            ? `${addr.street || ""}, ${addr.city || ""}, ${addr.zip || ""}`
            : "",
      });
      setChild(patient.child || false);
    }
  }, [patient]);

  console.log("patient", patient);

  // useEffect(() => {
  //   if (debouncedBirthDate && isFullDateEntered(debouncedBirthDate)) {
  //     const age = calculateAge(debouncedBirthDate);
  //     setChild(age < tenantAdultAge); // Set child to true if the patient is under the adult age
  //     setShowRepresentative(age < tenantAdultAge); // Show representative if the patient is under the adult age
  //   } else {
  //     setShowRepresentative(false); // Hide if the date isn't fully entered or valid
  //   }
  // }, [debouncedBirthDate, tenantAdultAge]);

  useEffect(() => {
    if (debouncedBirthDate && isFullDateEntered(debouncedBirthDate)) {
      const age = calculateAge(debouncedBirthDate);
      const isNowChild = age < tenantAdultAge;

      setChild(isNowChild); // Update `child` state
      setShowRepresentative(isNowChild); // Show representative if they are still a child

      if (!isNowChild) {
        // Reset representative info if the patient is no longer a child
        setRepresentativeFirstName("");
        setRepresentativeLastName("");
        setRepresentativePhone("");
        setRepresentativeEmail("");
        setRepresentativeType("");
      }
    } else {
      setShowRepresentative(false); // Hide if the date isn't fully entered or valid
    }
  }, [debouncedBirthDate, tenantAdultAge]);

  // LOGS

  // console.log("patient", patient);

  // console.log("patient phone", patient.phone);

  return (
    <>
      {loadingDisplay || !patient ? (
        <Flex bg={whiteMode} align="center" justify="center" w="100%" h="100%">
          <Spinner color={primary} size="lg" />
        </Flex>
      ) : (
        <Box
          position="relative"
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.2 } }}
          w="100%"
          h="100%"
        >
          <form
            onSubmit={submitHandler}
            style={{
              height: "100%",
            }}
          >
            <Flex w="100%" h="100%" direction="column">
              {/* HEADER START */}
              <Flex
                justify="space-between"
                align="center"
                p="1.25rem 2rem 1.25rem"
                borderBottom={`1px solid ${newBorderMode}`}
                mb="0rem"
                position="relative"
                zIndex={90}
                w="100%"
              >
                <Flex align="center">
                  {!isMobile && (
                    <IconButton
                      bg={backgroundLightMode}
                      border={`1px solid ${backgroundLightMode}`}
                      color={textSecondary}
                      icon={<FaArrowLeftLong />}
                      borderRadius="10.75rem"
                      fontSize="0.875rem"
                      onClick={closeHandler}
                      mr="1rem"
                      _hover={{
                        bg: primaryAlt,
                        color: white,
                        border: primaryAlt,
                      }}
                    />
                  )}

                  <Flex align="center">
                    <Heading
                      fontSize={["1rem", "1.125rem"]}
                      color={textPrimaryMode}
                      fontWeight="600"
                      mr={4}
                    >
                      {t("patient.editPatient")}
                    </Heading>
                    {/* {status} */}
                  </Flex>
                </Flex>

                <Flex align="center" justify="flex-end" gap="1rem">
                  {status === "new" && (
                    <Tooltip
                      p="0.5rem 1rem"
                      borderRadius="1rem"
                      label={
                        <Flex
                          direction="column"
                          justify="center"
                          align="center"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="500"
                            textAlign="center"
                          >
                            {t("patient.newStatusExplanation")}
                          </Text>
                        </Flex>
                      }
                    >
                      <Flex align="center">
                        <Icon
                          as={FaCircleInfo}
                          fontSize="0.875rem"
                          color={primaryAlt}
                        />
                      </Flex>
                    </Tooltip>
                  )}
                  <StatusButtonNew
                    isSmall
                    isUnclickable
                    currentStatus={status}
                    options="patient"
                    size={["sm", "md"]}
                    p={["0 1rem", "0 2rem"]}
                    fontSize={["0.75rem", "0.875rem"]}
                    onUpdateStatus={setStatus}
                  />

                  {isMobile && (
                    <IconButton
                      position="relative"
                      // top="1.5rem"
                      // right="1.5rem"
                      size="sm"
                      bg={backgroundLight}
                      color={textSecondary}
                      onClick={closeHandler}
                      borderRadius="10rem"
                      _hover={{ bg: primaryAlt, color: white }}
                    >
                      <Icon as={FaXmark} />
                    </IconButton>
                  )}
                </Flex>
              </Flex>
              {/* HEADER END */}

              {/* MAIN START */}
              <Flex
                w="100%"
                h="100%"
                direction="column"
                overflowY="auto" // Scrollbar
                p={["1rem 0rem 7rem", "1rem 2rem 7rem"]}
              >
                {/* PATIENT DETAILS START */}
                <Flex direction="column" w="100%">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                    p={["0 0 0 1.5rem", "initial"]}
                  >
                    {t("patient.patient")}
                  </Text>
                  <Flex
                    direction="column"
                    w="100%"
                    bg={backgroundLight}
                    borderRadius={["0rem", "1rem"]}
                    p="1.25rem 1.5rem 0.25rem"
                  >
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlDefault
                        autoFocus
                        type="text"
                        size="lg"
                        labelFontSize="0.8rem"
                        isRequired
                        label={t("form.firstName")}
                        value={firstName}
                        placeholder={t("placeholder.firstName")}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                        }}
                        isError={isFirstNameError}
                        w={["100%", "50%", "50%"]}
                        errorMessage={t("formError.firstNameRequired")}
                      />

                      <FormControlDefault
                        type="text"
                        size="lg"
                        labelFontSize="0.8rem"
                        isRequired
                        label={t("form.lastName")}
                        value={lastName}
                        placeholder={t("placeholder.lastName")}
                        onChange={(e) => {
                          setLastName(e.target.value);
                        }}
                        isError={isLastNameError}
                        w={["100%", "50%", "50%"]}
                        errorMessage={t("formError.lastNameRequired")}
                      />
                    </Flex>

                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlDatePicker
                        type="date"
                        size="lg"
                        labelFontSize="0.8rem"
                        isRequired
                        label={t("form.birthDate")}
                        minDate={minBirthDate}
                        maxDate={maxBirthDate}
                        value={birthDate}
                        // value="1970-01-01T00:00:34.357Z"
                        placeholder={t("placeholder.birthDate")}
                        onChange={(newValue) => {
                          setBirthDate(newValue);
                        }}
                        onOpen={() => {}}
                        isError={isBirthDateError}
                        w={["100%", "50%", "50%"]}
                        errorMessage={t("formError.birthDateRequired")}
                      />

                      <FormControlDefault
                        type="text"
                        size="lg"
                        labelFontSize="0.8rem"
                        // isRequired
                        label={t("form.personalCode")}
                        value={personalCode}
                        placeholder={t("placeholder.personalCode")}
                        onChange={(e) => {
                          setPersonalCode(e.target.value);
                        }}
                        // isError={isPersonalCodeError}
                        w={["100%", "50%", "50%"]}
                        // errorMessage={t("formError.personalCodeRequired")}
                      />
                    </Flex>

                    <Flex
                      w="100%"
                      direction={["column", "row", "row"]}
                      pr="2rem"
                    >
                      <FormControlGender
                        value={gender}
                        // isRequired
                        label={t("patient.gender")}
                        labelFontSize="0.8rem"
                        placeholder={t("placeholder.gender")}
                        onChange={handleGenderChange}
                        w={["100%", "100%", "50%"]}
                        zIndex={90005}
                      />
                    </Flex>
                  </Flex>
                </Flex>
                {/* PATIENT DETAILS END */}

                {/* REPRESENTATIVE DETAILS START */}
                {showRepresentative && (
                  <Flex
                    direction="column"
                    w="100%"
                    mt="1.5rem"
                    as={motion.div}
                    initial={{ opacity: 0, scaleY: 0 }}
                    animate={{
                      opacity: 1,
                      scaleY: 1,
                      transition: { duration: 0.2 },
                    }}
                    style={{ transformOrigin: "top" }}
                  >
                    <Flex align="center" m="0 0 0.5rem">
                      <Text
                        fontSize="0.875rem"
                        fontWeight="600"
                        color={textPrimaryMode}
                        mr={2}
                        p={["0 0 0 1.5rem", "initial"]}
                      >
                        {t("patient.representativeInfo")}
                      </Text>
                      <Tooltip
                        p="0.5rem 1rem"
                        borderRadius="1rem"
                        placement="top"
                        label={
                          <Flex direction="column" align="center">
                            <Text fontSize="xs" fontWeight="500">
                              {t("patient.representativeInfoTooltip")}
                            </Text>
                          </Flex>
                        }
                      >
                        <Tooltip
                          p="0.5rem 1rem"
                          borderRadius="1rem"
                          placement="top"
                          label={
                            <Flex
                              direction="column"
                              align="center"
                              justify="center"
                            >
                              <Text fontSize="xs" fontWeight="500">
                                {t("patient.representativeInfoTooltip")}
                              </Text>
                            </Flex>
                          }
                        >
                          <Flex align="center">
                            <Icon
                              as={FaCircleInfo}
                              fontSize="0.875rem"
                              color={primaryAlt}
                            />
                          </Flex>
                        </Tooltip>
                      </Tooltip>
                    </Flex>
                    <Flex
                      direction="column"
                      w="100%"
                      bg={backgroundLight}
                      borderRadius="1rem"
                      p="1.25rem 1.5rem"
                    >
                      <Flex w="100%" align="center" justify="space-between">
                        <FormControlRepresentativeType
                          // isRequired
                          value={representativeType}
                          label={t("patient.representativeType")}
                          placeholder={t("placeholder.representativeType")}
                          onChange={handleRepresentativeTypeChange}
                        />
                      </Flex>

                      <Flex
                        gap={["0rem", "2rem", "2rem"]}
                        w="100%"
                        direction={["column", "row", "row"]}
                      >
                        <FormControlDefault
                          type="text"
                          size="lg"
                          labelFontSize="0.8rem"
                          // isRequired
                          label={t("patient.representativeFirstName")}
                          value={representativeFirstName}
                          placeholder={t("placeholder.representativeFirstName")}
                          onChange={(e) => {
                            setRepresentativeFirstName(e.target.value);
                          }}
                          // isError={isRepresentativeFirstNameError}
                          w={["100%", "50%", "50%"]}
                          errorMessage={t(
                            "formError.representativeFirstNameRequired"
                          )}
                        />

                        <FormControlDefault
                          type="text"
                          size="lg"
                          labelFontSize="0.8rem"
                          // isRequired
                          label={t("patient.representativeLastName")}
                          value={representativeLastName}
                          placeholder={t("placeholder.representativeLastName")}
                          onChange={(e) => {
                            setRepresentativeLastName(e.target.value);
                            // setLastNameTouched(true);
                          }}
                          // isError={isRepresentativeLastNameError}
                          w={["100%", "50%", "50%"]}
                          errorMessage={t(
                            "formError.representativeLastNameRequired"
                          )}
                        />
                      </Flex>

                      <Flex
                        gap={["0rem", "2rem", "2rem"]}
                        w="100%"
                        direction={["column", "row", "row"]}
                      >
                        <FormControlPhone
                          label={t("patient.representativePhone")}
                          size="lg"
                          labelFontSize="0.8rem"
                          value={representativePhone}
                          onChange={(newPhone) => {
                            setRepresentativePhone(newPhone);
                            // setPhoneTouched(true);
                          }}
                          // isRequired={true}
                          // isError={isRepresentativePhoneError}
                          errorMessage={t(
                            "formError.representativePhoneRequired"
                          )}
                          w={["100%", "50%", "50%"]}
                          defaultCountry="lt"
                          zIndex={90003}
                        />

                        <FormControlDefault
                          type="email"
                          size="lg"
                          labelFontSize="0.8rem"
                          // isRequired
                          label={t("patient.representativeEmail")}
                          value={representativeEmail}
                          placeholder={t("placeholder.representativeEmail")}
                          onChange={(e) => {
                            setRepresentativeEmail(e.target.value);
                            // setEmailTouched(true);
                          }}
                          // isTouched={emailTouched}
                          // isError={isRepresentativeEmailError}
                          w={["100%", "50%", "50%"]}
                          errorMessage={t(
                            "formError.representativeEmailRequired"
                          )}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                )}

                {/* REPRESENTATIVE DETAILS END */}

                {/* PATIENT ADDRESS START */}
                <Flex direction="column" w="100%" mt="1.5rem">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                    p={["0 0 0 1.5rem", "initial"]}
                  >
                    {isMobile
                      ? t("patient.addressContactDetailsMobile")
                      : t("patient.addressContactDetails")}
                  </Text>
                  <Flex
                    direction="column"
                    w="100%"
                    bg={backgroundLight}
                    borderRadius={["0rem", "1rem"]}
                    p="1.25rem 1.5rem"
                  >
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      {/* SIMPLY SHOW PHONE START */}
                      {/* <Box w="100%">
                        {console.log("Box phone value:", phone)}
                        {phone}
                      </Box> */}
                      {/* SIMPLY SHOW PHONE END */}

                      <FormControlPhone
                        label={t("form.phone")}
                        size="lg"
                        labelFontSize="0.8rem"
                        value={phone}
                        onChange={(newPhone) => {
                          setPhone(newPhone);
                        }}
                        // isRequired={true}
                        isError={isPhoneError}
                        errorMessage={t("formError.phoneRequired")}
                        w={["100%", "50%", "50%"]}
                        defaultCountry="lt"
                        zIndex={90002}
                      />

                      <FormControlPhone
                        label={t("form.phoneAlt")}
                        size="lg"
                        labelFontSize="0.8rem"
                        value={phoneAlt}
                        onChange={(newPhone) => {
                          setPhoneAlt(newPhone);
                        }}
                        isRequired={false}
                        errorMessage={t("formError.phoneRequired")}
                        w={["100%", "50%", "50%"]}
                        defaultCountry="lt"
                        zIndex={90000}
                      />
                    </Flex>

                    <FormControlDefault
                      type="email"
                      size="lg"
                      labelFontSize="0.8rem"
                      // isRequired
                      label={t("form.email")}
                      value={email}
                      placeholder={t("placeholder.email")}
                      onChange={(e) => {
                        setEmail(e.target.value);
                      }}
                      // isError={isEmailValidated}
                      w="100%"
                      errorMessage={t("formError.emailRequired")}
                    />

                    {!manualAddressVisible && (
                      <Flex direction="column" w="100%" p="0rem">
                        <FormControlAddressFull
                          label={t("form.address")}
                          size="lg"
                          labelFontSize="0.8rem"
                          placeholder={t("placeholder.address")}
                          onAddressSelect={handleAddressSelect}
                          // isRequired={true}
                          currentValue={address.display}
                          w="100%"
                          googleApiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                          zIndex={90001}
                        />
                        <Flex
                          align="center"
                          justify="center"
                          gap="1rem"
                          p="0.5rem 0 0"
                        >
                          <Flex
                            position="relative"
                            m="0rem 0 0"
                            justify="flex-end"
                            w="100%"
                          >
                            <Button
                              bg="transparent"
                              onClick={toggleManualAddressVisible}
                              color={primary}
                              fontSize="xs"
                              size="xs"
                              fontWeight="500"
                              p="0.25rem 0.75rem"
                              borderRadius="10rem"
                              opacity="1"
                              transition="all 0.3s"
                              _hover={{
                                bg: white,
                                opacity: "1",
                              }}
                            >
                              {t("onboarding.enterAddressManual")}
                            </Button>
                          </Flex>
                        </Flex>
                      </Flex>
                    )}

                    {manualAddressVisible && (
                      <>
                        <Flex
                          direction="column"
                          align="center"
                          justify="center"
                          p="0rem"
                        >
                          <HStack gap={4} w="100%">
                            <FormControlDefault
                              type="text"
                              size="lg"
                              labelFontSize="0.8rem"
                              // isRequired={true}
                              label={t("form.street")}
                              value={address.street}
                              placeholder={t("placeholder.street")}
                              onChange={(e) =>
                                setAddress({
                                  ...address,
                                  street: e.target.value,
                                })
                              }
                              w={["100%", "50%", "50%"]}
                            />

                            <FormControlDefault
                              type="text"
                              // isRequired={true}
                              size="lg"
                              labelFontSize="0.8rem"
                              label={t("form.zip")}
                              value={address.zip}
                              placeholder={t("placeholder.zip")}
                              onChange={(e) =>
                                setAddress({ ...address, zip: e.target.value })
                              }
                              w={["100%", "50%", "50%"]}
                            />
                          </HStack>

                          <HStack gap={4} w="100%">
                            <FormControlDefault
                              type="text"
                              // isRequired={true}
                              size="lg"
                              labelFontSize="0.8rem"
                              label={t("form.city")}
                              value={address.city}
                              placeholder={t("placeholder.city")}
                              onChange={(e) =>
                                setAddress({ ...address, city: e.target.value })
                              }
                              w={["100%", "50%", "50%"]}
                              errorMessage={t("formError.cityRequired")}
                            />

                            <FormControlCountry
                              label={t("form.country")}
                              size="lg"
                              labelFontSize="0.8rem"
                              value={address.country}
                              // isRequired={true}
                              onChange={(selectedOption) => {
                                setAddress(
                                  Object.assign({}, address, {
                                    country: selectedOption.value,
                                  })
                                );
                              }}
                              w={["100%", "50%", "50%"]}
                              errorMessage={t("formError.countryRequired")}
                            />
                          </HStack>
                          <Flex align="center" justify="center" gap="1rem">
                            <Flex
                              position="relative"
                              m="0rem 0 0"
                              justify="center"
                            >
                              <Button
                                bg="transparent)"
                                onClick={toggleManualAddressVisible}
                                color={primary}
                                variant="link"
                                fontSize="xs"
                                fontWeight="500"
                                p="0.25rem 0.75rem"
                                borderRadius="10rem"
                                _hover={{
                                  bg: white,
                                }}
                              >
                                {t("onboarding.enterAddressGoogle")}
                              </Button>
                            </Flex>
                          </Flex>
                        </Flex>
                      </>
                    )}
                  </Flex>
                </Flex>
                {/* PATIENT ADDRESS END */}

                {/* BOTTOM START */}
                <Flex
                  align="center"
                  justify="flex-end"
                  position={["relative", "fixed"]}
                  bottom={["initial", "2rem"]}
                  right={["initial", "2rem"]}
                  gap="0.5rem"
                  zIndex={99999999}
                  p={["1.5rem 1.5rem", "initial"]}
                >
                  <Button
                    // minWidth="10rem"
                    bg={backgroundLightMode}
                    size="lg"
                    px="2rem"
                    w={["50%", "initial"]}
                    color={textSecondary}
                    fontSize="sm"
                    borderRadius="10rem"
                    border={`1px solid ${whiteMode}`}
                    _hover={{
                      bg: primaryAlt,
                      color: white,
                      border: `1px solid ${whiteMode}`,
                    }}
                    onClick={closeHandler}
                  >
                    {t("button.cancel")}
                  </Button>

                  <Tooltip
                    bg={buttonRed}
                    color={buttonRedText}
                    padding="0.5rem 1rem"
                    borderRadius="1rem"
                    placement="top"
                    label={
                      <Flex direction="column" justify="center" align="center">
                        {isFirstNameError && (
                          <Text
                            color={buttonRedText}
                            fontSize="xs"
                            fontWeight="500"
                          >
                            {t("formError.firstNameRequired")}
                          </Text>
                        )}
                        {isLastNameError && (
                          <Text
                            color={buttonRedText}
                            fontSize="xs"
                            fontWeight="500"
                          >
                            {t("formError.lastNameRequired")}
                          </Text>
                        )}
                        {isBirthDateError && (
                          <Text
                            color={buttonRedText}
                            fontSize="xs"
                            fontWeight="500"
                          >
                            {t("formError.birthDateRequired")}
                          </Text>
                        )}
                        {/* {isPersonalCodeError && (
                                      <Text
                                        color={buttonRedText}
                                        fontSize="xs"
                                        fontWeight="500"
                                      >
                                        {t("formError.personalCodeRequired")}
                                      </Text>
                                    )} */}
                        {isGenderError && (
                          <Text
                            color={buttonRedText}
                            fontSize="xs"
                            fontWeight="500"
                          >
                            {t("formError.genderRequired")}
                          </Text>
                        )}

                        {isPhoneShortError && (
                          <Text
                            color={buttonRedText}
                            fontSize="xs"
                            fontWeight="500"
                          >
                            {t("formError.phoneRequired")}
                          </Text>
                        )}

                        {isEmailValidated && (
                          <Text
                            color={buttonRedText}
                            fontSize="xs"
                            fontWeight="500"
                          >
                            {t("formError.emailRequired")}
                          </Text>
                        )}

                        {isAddressError && (
                          <Text
                            color={buttonRedText}
                            fontSize="xs"
                            fontWeight="500"
                          >
                            {t("formError.addressRequired")}
                          </Text>
                        )}

                        {child && isRepresentativeInfoError && (
                          <Text
                            color={buttonRedText}
                            fontSize="xs"
                            fontWeight="500"
                          >
                            {t("formError.representativeInformationRequired")}
                          </Text>
                        )}
                      </Flex>
                    }
                    isDisabled={
                      !isFirstNameError && !isLastNameError && !isBirthDateError
                      // !isPhoneShortError &&
                      // !isEmailValidated &&
                      // !isGenderError &&
                      // !isAddressError &&
                      // (!child || !isRepresentativeInfoError)
                    }
                  >
                    <Button
                      type="submit"
                      minWidth="10rem"
                      bg={textPrimaryMode}
                      size="lg"
                      w={["50%", "initial"]}
                      color={whiteMode}
                      fontSize="sm"
                      borderRadius="10rem"
                      border={`1px solid ${whiteMode}`}
                      _hover={{
                        bg: primaryAlt,
                        color: white,
                        border: `1px solid ${whiteMode}`,
                      }}
                      onClick={submitHandler}
                      isDisabled={
                        isFirstNameError || isLastNameError || isBirthDateError
                        // isPhoneShortError ||
                        // isEmailValidated ||
                        // isGenderError ||
                        // isAddressError ||
                        // (child && isRepresentativeInfoError)
                      }
                    >
                      {t("patient.editPatient")}
                    </Button>
                  </Tooltip>
                </Flex>

                {/* BOTTOM END */}
              </Flex>
              {/* MAIN END */}
            </Flex>
          </form>
        </Box>
      )}
    </>
  );
};

export default EditPatientNew;
